import './styles/Notifications.css';

import {
  removeAllNotifications,
  removeNotification,
} from 'containers/Toaster/actions';
import { makeSelectMessages } from 'containers/Toaster/selectors';
import { Fragment, PureComponent } from 'react';

import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { date } from '@ipfabric/utils';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import handleEvent from 'utils/handleEvent';
import { withAppProps } from 'utils/react/withAppProps';
import { ClickOutsideDropdownHandler } from '../ClickOutsideDropdownHandler';
import { snoozeNotification } from './snoozedNotifications';

@withAppProps
class NotificationsHeader extends PureComponent {
  static propTypes = {
    dateTimeFormat: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    messages: ImmutablePropTypes.list.isRequired,
    msg: PropTypes.object.isRequired,
    onRemoveNotification: PropTypes.func.isRequired,
    onRemoveAllNotifications: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
  };

  getMessageIcon = (message) => {
    switch (message.get('type')) {
      case 30:
      case 20:
        return 'fa fa-exclamation-triangle fa-fw';
      case 10:
        return 'fa fa-info-circle fa-fw';
      case 0:
        return 'fa fa-check fa-fw';
      default:
        return '';
    }
  };

  getColorClass = (message) => {
    switch (message.get('type')) {
      case 0:
        return 'text-success';
      case 10:
        return 'text-info';
      case 20:
        return 'text-warning';
      case 30:
        return 'text-danger';
      default:
        return '';
    }
  };

  getNotificationIconColor = (unreadMessages) => {
    if (unreadMessages.filter((message) => message.get('type') === 30).size) {
      return 'danger';
    }
    if (unreadMessages.filter((message) => message.get('type') === 20).size) {
      return 'warning';
    }
    if (unreadMessages.filter((message) => message.get('type') === 10).size) {
      return 'info';
    }
    if (unreadMessages.filter((message) => message.get('type') === 0).size) {
      return 'success';
    }
    return '';
  };

  getNotificationIcon = () => {
    const { messages } = this.props;
    const unreadMessages = messages.filter(
      (message) => !message.get('read') && !message.get('flashOnly'),
    );
    if (unreadMessages.size) {
      return (
        <span
          className={classNames(
            'label',
            `label-${this.getNotificationIconColor(unreadMessages)}`,
            'header-notifications__bell-wrapper',
          )}
        >
          <i id='header-notifications-messages-icon' className='fa fa-bell' />
        </span>
      );
    }
    return null;
  };

  sortMessages = (a, b) => {
    return b.get('time') - a.get('time');
  };

  makeHandleRemoveMessage = (id) => () => {
    setTimeout(() => {
      this.props.onRemoveNotification(id);
    }, 0);
  };

  handleRemoveAll = handleEvent(() => {
    setTimeout(() => {
      this.props.onRemoveAllNotifications();
      this.props.toggle();
    }, 0);
  });

  renderMessages = (messages) => {
    const {
      msg: { notifications: msg },
      dateTimeFormat,
      timezone,
    } = this.props;
    return messages.map((message) => {
      const messageText = message.get('message', '');
      const text =
        typeof messageText === 'function'
          ? messageText(this.props.toggle)
          : messageText;
      const snoozeKey = message.get('snoozeKey', false);

      return (
        <div
          key={message.get('hash')}
          className='header-dropdown__content-item'
        >
          <div
            className={classNames(
              this.getColorClass(message),
              'header-dropdown__job-icon-wrapper',
            )}
          >
            <i className={this.getMessageIcon(message)} />
          </div>
          <div>
            <div
              className={classNames(
                this.getColorClass(message),
                'font-semibold',
              )}
            >
              {message.get('title') || text}
            </div>
            <div className={message.get('type') === 30 ? 'text-danger' : ''}>
              {message.get('title') ? message.get('message', '') : ''}
            </div>
            <div className='header-dropdown__content-detail'>
              {date.formatUtcTimestampInTimeZone(
                message.get('time'),
                timezone,
                dateTimeFormat,
              )}
              {snoozeKey && (
                <ButtonAsA
                  onClick={(e) => {
                    snoozeNotification(snoozeKey);
                    this.makeHandleRemoveMessage(message.get('hash'))(e);
                  }}
                >
                  {get(msg, 'stopReportingThat')}
                </ButtonAsA>
              )}
            </div>
          </div>
          <div className='header-dropdown__action-wraper'>
            <Tooltip content={get(msg, 'clearTooltip')}>
              <Button
                type='button'
                variant='tertiary'
                onClick={this.makeHandleRemoveMessage(message.get('hash'))}
                icon='fa-xmark'
              />
            </Tooltip>
          </div>
        </div>
      );
    });
  };

  render() {
    const {
      msg: { notifications: msg },
      messages,
    } = this.props;
    const displayMessages = messages
      ?.filter((message) => !message.get('flashOnly'))
      .sort(this.sortMessages);
    return (
      <ClickOutsideDropdownHandler
        className='dropdown header-dropdown__dropdown-li'
        open={this.props.open}
        toggle={this.props.toggle}
      >
        <ButtonAsA
          className='dropdown-toggle'
          onClick={this.props.toggle}
          id='header-notifications-toggle'
          // biome-ignore lint/a11y/useSemanticElements: Why is this rule even a thing - "combobox" isn't even supposed to be used on a select element..
          role='combobox'
          aria-haspopup
          aria-expanded={this.props.open}
          aria-label='Notifications'
          aria-controls='dropdown-notifications'
        >
          <i className='fa fa-envelope' />
          {this.getNotificationIcon()}
        </ButtonAsA>
        <ul
          className={classNames(
            'dropdown-menu dropdown-notifications header-dropdown__dropdown-menu',
            this.props.open && 'dropdown-menu--open',
          )}
          id='dropdown-notifications'
          role='menu'
          aria-label='Notifications'
        >
          {displayMessages?.size ? (
            <Fragment>
              <div className='header header-dropdown__content-header header-dropdown__content-header--border header-dropdown__content-header--with-button flow-root items-center justify-between'>
                {get(msg, 'title')}
                <Button
                  type='button'
                  variant='secondary'
                  size='xxs'
                  className='pull-right'
                  onClick={this.handleRemoveAll}
                >
                  {get(msg, 'btnClearAll')}
                </Button>
              </div>
              {displayMessages.size
                ? this.renderMessages(displayMessages)
                : null}
            </Fragment>
          ) : (
            <div className='header header-dropdown__content-header'>
              {get(msg, 'noMessages')}
            </div>
          )}
        </ul>
      </ClickOutsideDropdownHandler>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  messages: makeSelectMessages(),
});

function mapDispatchToProps(dispatch) {
  return {
    onRemoveNotification: (args) => dispatch(removeNotification(args)),
    onRemoveAllNotifications: () => dispatch(removeAllNotifications()),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(NotificationsHeader);
